import { useState, useEffect } from 'react';

/**
 * useDebounce
 *
 * A custom React hook that delays updating the debounced value until
 * after a specified delay has passed since the last change.
 *
 * Useful for reducing the frequency of API calls or expensive computations
 * while the user is typing or interacting rapidly.
 *
 * @template T - The type of the input value (can be string, number, object, etc.)
 * @param {T} value - The input value to debounce
 * @param {number} delay - Delay time in milliseconds
 * @returns {T} - The debounced value that updates after the delay
 *
 * @example
 * const debouncedSearch = useDebounce(searchTerm, 300);
 */
export function useDebounce<T>(value: T, delay: number): T {
  // State to store the debounced value
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    // Set a timeout to update the debounced value after the specified delay
    const handler = setTimeout(() => {
      setDebounced(value);
    }, delay);

    // If the value changes before the delay ends, clear the previous timeout
    // This prevents updating too frequently while the user is still typing
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]); // Re-run this effect if `value` or `delay` changes

  return debounced;
}
